"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { areCurrenciesEqual, CurrencyIdentifierFragment, CurrencyLockupFragment, currencyToAssetIdentifier, readCurrencyFragment, readCurrencyIdentifierFragment, useChains, useCurrencies } from "@opensea/app-kit";
import { graphql, useQuery } from "@opensea/graphql";
import { useDebounceValue, useLocalStorage, useVisibilityChange } from "@opensea/ui-kit/hooks";
import { bn } from "@opensea/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";
import { ActionTimelineFragment } from "../../../../components/ActionTimeline";
import { SmartContractWalletWarningModal } from "../../../../components/SmartContractWalletWarningModal";
import { useCurrencyByContract } from "../../../../hooks/useCurrencyByContract";
import { useSyncFormFields } from "../../../../hooks/useSyncFormField";
import { useAddress, useChainArch, useChainIdentifier } from "../../../../polychain/hooks";
import { useBalances } from "../../../../providers/BalancesProvider";
import { FlowActions } from "../../../../stores/flowStore";
import { useIsSmartContractWallet } from "../../../hooks/useIsSmartContractWallet";
import { SWAP_IMPACT_WARNING_THRESHOLD } from "../components/constants";
import { SwapDetailsFragment } from "../components/SwapDetails";
import { getDefaultSlippage, getMaxSlippageStorageKey, USD_INPUT_MODE_PREFERENCE_LOCAL_STORAGE_KEY } from "../constants";
import { useTrackSwapEventFragment } from "../hooks/use-track-swap-event.fragment";
import { useSwapGasCheckFragment } from "../hooks/useSwapGasCheck.fragment";
import { useSwapReceipt } from "../hooks/useSwapReceipt";
import { swapFlowFormSchema } from "../schema";
import { readSwapImpact } from "./readSwapImpact";
import { readSwapImpactFragment } from "./readSwapImpact.fragment";
import { SwapContext } from "./SwapContext";
import { useCanLoadQuote } from "./use-can-load-quote";
const MINIMUM_RETURN_VALUE_USD_RATIO = 0.8;
const MINIMUM_RETURN_USD_ALLOWED_VARIANCE = 1;
const SWAP_QUOTE_QUERY = graphql(`
    query useSwapQuote(
      $fromAssets: [AssetQuantityInput!]!
      $toAssets: [AssetQuantityInput!]!
      $address: Address!
      $slippageTolerance: Float
      $recipient: Address
    ) {
      tokenSwap(
        fromAssets: $fromAssets
        toAssets: $toAssets
        address: $address
        slippageTolerance: $slippageTolerance
        recipient: $recipient
      ) {
        quote {
          slippageTolerance
          swapRoutes {
            __typename
            ... on SwapRoute {
              swapProvider
              toQuantity
              fromQuantity
              toAsset {
                __typename
                ... on Currency {
                  contractAddress
                  symbol
                  decimals
                  warnings {
                    featureId
                  }
                  ...CurrencyLockup
                }
                ...CurrencyIdentifier
              }
              fromAsset {
                ... on Currency {
                  contractAddress
                  symbol
                  decimals
                  warnings {
                    featureId
                  }
                  ...CurrencyLockup
                }
                ...CurrencyIdentifier
              }
              pricePerFromAsset {
                usd
              }
              pricePerToAsset {
                usd
              }
              ...readSwapImpact
            }
          }
          ...SwapDetails
          ...useTrackSwapEvent
          ...useSwapGasCheckFragment
        }
        swapActions {
          errors {
            __typename
          }
          actions {
            ...ActionTimeline
          }
        }
      }
    }
  `, [SwapDetailsFragment, CurrencyIdentifierFragment, CurrencyLockupFragment, useTrackSwapEventFragment, useSwapGasCheckFragment, readSwapImpactFragment, ActionTimelineFragment]);
const OPERATION_CONTEXT = {
  suspense: false
};
export function SwapProvider({
  children,
  initialFromCurrency,
  initialToCurrency,
  isLoadingInitialFromCurrency,
  isLoadingInitialToCurrency,
  variant = "default",
  isNFTStrategyCurrencySelectionDisabled = false,
  defaultToUsdInput = false,
  initialTransactionMode = "sell",
  source
}) {
  const t = useTranslations("wallet.SwapProvider");
  const address = useAddress();

  // Transaction mode state (buy or sell)
  const [transactionMode, setTransactionModeState] = useState(initialTransactionMode);

  // Read USD input mode preference from localStorage
  const [storedUsdInputPreference, setStoredUsdInputPreference] = useLocalStorage(USD_INPUT_MODE_PREFERENCE_LOCAL_STORAGE_KEY, undefined);

  // Compute initial USD input mode: localStorage takes priority, then defaultToUsdInput
  const initialUsdInputMode = storedUsdInputPreference || defaultToUsdInput;
  const [isLoadingInitialFromCurrencyData, setIsLoadingInitialFromCurrencyData] = useState(isLoadingInitialFromCurrency || Boolean(initialFromCurrency));
  const [isLoadingInitialToCurrencyData, setIsLoadingInitialToCurrencyData] = useState(isLoadingInitialToCurrency || Boolean(initialToCurrency));
  const [shouldShowConfirmationModal, setShouldShowConfirmationModal] = useState(false);
  const {
    currency: fromCurrencyData
  } = useCurrencyByContract({
    contract: {
      chain: initialFromCurrency?.chainIdentifier,
      address: initialFromCurrency?.contractAddress
    },
    pause: !initialFromCurrency ||
    // fromCurrency is not provided
    isLoadingInitialFromCurrency ||
    // fromCurrency is loading
    !isLoadingInitialFromCurrencyData // initial fromCurrency data is already loaded
  });
  const {
    currency: toCurrencyData
  } = useCurrencyByContract({
    contract: {
      chain: initialToCurrency?.chainIdentifier,
      address: initialToCurrency?.contractAddress
    },
    pause: !initialToCurrency ||
    // toCurrency is not provided
    isLoadingInitialToCurrency ||
    // toCurrency is loading
    !isLoadingInitialToCurrencyData // initial toCurrency data is already loaded
  });
  const defaultValues = useDefaultFormValues({
    isUsdInputMode: initialUsdInputMode,
    transactionMode
  });
  const methods = useForm({
    defaultValues,
    mode: "onChange",
    // @ts-expect-error - allow strict schema and relaxed defaults
    resolver: zodResolver(swapFlowFormSchema)
  });

  // Wrapper function to sync transaction mode with form
  const setTransactionMode = useCallback(mode => {
    setTransactionModeState(mode);
    methods.setValue("transactionMode", mode);
  }, [methods]);

  // Update form values when currency data is loaded
  useEffect(() => {
    // Set fromCurrency if only fromCurrency is provided
    if (fromCurrencyData) {
      methods.setValue("fromCurrency", readCurrencyFragment(fromCurrencyData));
      setIsLoadingInitialFromCurrencyData(false);
    }
    // Set toCurrency if only toCurrency is provided
    if (toCurrencyData) {
      methods.setValue("toCurrency", readCurrencyFragment(toCurrencyData));
      setIsLoadingInitialToCurrencyData(false);
    }
  }, [fromCurrencyData, methods, toCurrencyData, setIsLoadingInitialFromCurrencyData, setIsLoadingInitialToCurrencyData]);
  const [swapQuote, setSwapQuote] = useState();
  const [fromCurrency, fromQuantity, toCurrency, toQuantity, maxSlippage, recipientAddress, customDestinationWalletEnabled, formTransactionMode] = useWatch({
    control: methods.control,
    name: ["fromCurrency", "fromQuantity", "toCurrency", "toQuantity", "maxSlippage", "recipient.resolvedAddress", "customDestinationWalletEnabled", "transactionMode"]
  });

  // Sync transaction mode to form when it changes
  useEffect(() => {
    if (formTransactionMode !== transactionMode) {
      methods.setValue("transactionMode", transactionMode);
    }
  }, [transactionMode, formTransactionMode]);

  // Clear quote and track mode change time
  useEffect(() => {
    modeChangedAtRef.current = Date.now();
    setSwapQuote(undefined);
    // Also ensure form values are cleared immediately on mode change
    // This helps prevent debounced values from persisting
    methods.resetField("fromQuantity");
    methods.resetField("toQuantity");
    methods.clearErrors();
  }, [transactionMode, methods]);
  useEffect(() => {
    FlowActions.setFlowField("fromCurrency", fromCurrency);
    FlowActions.setFlowField("toCurrency", toCurrency);
    FlowActions.setFlowField("fromAmount", fromQuantity?.toString());
    FlowActions.setFlowField("toAmount", toQuantity?.toString());
    FlowActions.setFlowField("maxSlippage", maxSlippage);
  }, [fromCurrency, toCurrency, fromQuantity, toQuantity, maxSlippage]);

  // Watch for USD input mode changes to persist user preference
  const isUsdInputMode = useWatch({
    control: methods.control,
    name: "isUsdInputMode"
  });
  const prevIsUsdInputModeRef = useRef(undefined);
  const modeChangedAtRef = useRef(0);
  useEffect(() => {
    const prevValue = prevIsUsdInputModeRef.current;
    const currentValue = isUsdInputMode;

    // Save to localStorage ONLY when user toggles from crypto (false) to USD (true)
    if (prevValue === false && currentValue === true) {
      setStoredUsdInputPreference(true);
    }
    // Remove preference when user toggles from USD (true) back to crypto (false)
    if (prevValue === true && currentValue === false) {
      setStoredUsdInputPreference(undefined);
    }

    // Update ref for next comparison
    prevIsUsdInputModeRef.current = currentValue;
  }, [isUsdInputMode, setStoredUsdInputPreference]);

  // Sync fromCurrency balance to form data
  const [isAutoSlippageEnabled, setIsAutoSlippageEnabled] = useState(true);
  const {
    getBalanceOfCurrencyBySymbol
  } = useBalances();
  const fromCurrencyBalance = getBalanceOfCurrencyBySymbol({
    chainIdentifier: fromCurrency.chainIdentifier,
    symbol: fromCurrency.symbol
  });
  useSyncFormFields({
    fields: [{
      name: "fromCurrencyBalance",
      value: bn(fromCurrencyBalance?.quantity ?? 0).toNumber()
    },
    // Revalidate fromQuantity when fromCurrencyBalance changes
    {
      name: "fromQuantity",
      value: fromQuantity
    }],
    setValue: methods.setValue
  });

  // Fetch quote with debounced quantities
  const [debouncedFromQuantity] = useDebounceValue(fromQuantity, 200);
  const [debouncedToQuantity] = useDebounceValue(toQuantity, 200);
  const canLoadQuote = useCanLoadQuote(methods.watch);
  const nullAddress = fromCurrency.chainIdentifier === "solana" ? "11111111111111111111111111111111" : "0x0000000000000000000000000000000000000000";
  const chainArch = useChainArch();

  // Build quote variables based on transaction mode - memoized to prevent unnecessary re-renders
  const quoteVariables = useMemo(() => {
    if (transactionMode === "buy") {
      // User wants to buy a specific amount (toQuantity)
      return {
        fromAssets: [{
          asset: currencyToAssetIdentifier(fromCurrency)
        }],
        toAssets: [{
          asset: currencyToAssetIdentifier(toCurrency ?? {}),
          quantity: debouncedToQuantity?.toString() ?? "0"
        }],
        address: address ?? nullAddress,
        recipient: customDestinationWalletEnabled ? recipientAddress : address,
        slippageTolerance: isAutoSlippageEnabled ? undefined : maxSlippage ?? getDefaultSlippage(chainArch)
      };
    }
    // User wants to sell a specific amount (fromQuantity)
    return {
      fromAssets: [{
        asset: currencyToAssetIdentifier(fromCurrency),
        quantity: debouncedFromQuantity?.toString() ?? "0"
      }],
      toAssets: [{
        asset: currencyToAssetIdentifier(toCurrency ?? {})
      }],
      address: address ?? nullAddress,
      recipient: customDestinationWalletEnabled ? recipientAddress : address,
      slippageTolerance: isAutoSlippageEnabled ? undefined : maxSlippage ?? getDefaultSlippage(chainArch)
    };
  }, [transactionMode, fromCurrency, toCurrency, debouncedFromQuantity, debouncedToQuantity, address, recipientAddress, customDestinationWalletEnabled, nullAddress, isAutoSlippageEnabled, maxSlippage, chainArch]);

  // Determine if we should pause the query - memoized to prevent unnecessary re-renders
  const shouldPauseQuery = useMemo(() => {
    if (transactionMode === "buy") {
      return !(canLoadQuote && debouncedToQuantity && debouncedToQuantity > 0);
    }
    return !(canLoadQuote && debouncedFromQuantity) || debouncedFromQuantity === 0;
  }, [transactionMode, canLoadQuote, debouncedFromQuantity, debouncedToQuantity]);
  const [{
    data,
    fetching,
    error
  }, reexecuteQuery] = useQuery({
    query: SWAP_QUOTE_QUERY,
    variables: quoteVariables,
    pause: shouldPauseQuery,
    context: OPERATION_CONTEXT
  }, {
    throwOnError: false
  });

  // set errors based on any currency validation errors
  useEffect(() => {
    if (fromCurrency.chainIdentifier === toCurrency?.chainIdentifier && fromCurrency.contractAddress === toCurrency.contractAddress) {
      methods.setError("toQuantity", {
        message: "Cannot swap to the same token on the same chain"
      });
    } else {
      methods.clearErrors("toQuantity");
    }
  }, [fromCurrency, toCurrency, methods]);

  // clear actions when form values change
  useEffect(() => {
    setActions([]);
  }, [fromCurrency, toCurrency, recipientAddress, customDestinationWalletEnabled]);

  // refetch quote when recipient address changes
  useEffect(() => {
    if (recipientAddress && Boolean(fromQuantity)) {
      reexecuteQuery({
        requestPolicy: "network-only"
      });
    }
  }, [recipientAddress, reexecuteQuery]);

  // Set quantity based on quote response
  useEffect(() => {
    if (data?.tokenSwap) {
      // Skip if this is stale data from before the last mode change
      const quoteReceivedAt = Date.now();
      if (quoteReceivedAt < modeChangedAtRef.current) {
        return;
      }

      // Skip if we just cleared the form (both quantities are 0 or undefined)
      // This prevents stale data from repopulating fields after mode change
      if ((fromQuantity === 0 || fromQuantity === undefined) && (toQuantity === 0 || toQuantity === undefined || Number.isNaN(toQuantity))) {
        return;
      }
      const swapRoute = data.tokenSwap.quote.swapRoutes[0];
      switch (swapRoute.__typename) {
        case "SwapRoute":
          {
            if (swapRoute.fromAsset.__typename !== "Currency" || swapRoute.toAsset.__typename !== "Currency") {
              throw new Error("Expected swap route assets to be currencies");
            }

            // Validate that quote currencies match current form state
            const quoteFromAddress = swapRoute.fromAsset.contractAddress.toLowerCase();
            const quoteToAddress = swapRoute.toAsset.contractAddress.toLowerCase();
            const formFromAddress = fromCurrency?.contractAddress?.toLowerCase();
            const formToAddress = toCurrency?.contractAddress?.toLowerCase();

            // Skip if currencies don't match current form state
            if (quoteFromAddress !== formFromAddress || quoteToAddress !== formToAddress) {
              return;
            }

            // Update the calculated quantity based on transaction mode
            if (transactionMode === "buy") {
              // User specified toQuantity, update fromQuantity
              methods.setValue("fromQuantity", bn(swapRoute.fromQuantity, swapRoute.fromAsset.decimals).toNumber(), {
                shouldValidate: true
              });
            } else {
              // User specified fromQuantity, update toQuantity
              methods.setValue("toQuantity", bn(swapRoute.toQuantity, swapRoute.toAsset.decimals).toNumber(), {
                shouldValidate: true
              });
            }
            const fromUsdValue = bn(swapRoute.pricePerFromAsset.usd).toNumber();
            const toUsdValue = bn(swapRoute.pricePerToAsset.usd).toNumber();
            if (toUsdValue < fromUsdValue * MINIMUM_RETURN_VALUE_USD_RATIO && fromUsdValue - toUsdValue > MINIMUM_RETURN_USD_ALLOWED_VARIANCE) {
              methods.setError("toQuantity", {
                message: t("lowOutputAmount")
              });
            }

            // Check for high swap impact and set warning
            const swapImpact = readSwapImpact(swapRoute);
            if (swapImpact && bn(swapImpact.percent).lt(SWAP_IMPACT_WARNING_THRESHOLD)) {
              const percentDisplay = Math.abs(bn(swapImpact.percent).toNumber());
              const usdDisplay = Math.abs(bn(swapImpact.usd).toNumber());
              methods.setError("root.swapImpact", {
                message: t("highSwapImpact", {
                  percent: percentDisplay.toFixed(2),
                  usd: usdDisplay.toFixed(2)
                }),
                type: "warning"
              });
            } else {
              // Clear swap impact warning if it's acceptable
              methods.clearErrors("root.swapImpact");
            }
            return;
          }
        case "SwapRouteSwapOutputTooSmallToCoverFees":
          methods.setError("toQuantity", {
            message: t("outputTooLowForFees"),
            type: "warning"
          });
          return;
        case "SwapRouteNotFound":
          methods.setError("root.swapError", {
            message: t("noRouteFound"),
            type: "critical"
          });
          return;
        case "SwapRouteAssetNotFound":
          methods.setError("root.swapError", {
            message: t("noRouteFound"),
            type: "critical"
          });
          return;
        case "SwapRouteInsufficientFundsError":
          methods.setError("fromQuantity", {
            message: t("insufficientFunds"),
            type: "critical"
          });
          return;
        case "SwapRouteInsufficientLiquidityError":
          methods.setError("root.swapError", {
            message: t("insufficientLiquidity"),
            type: "critical"
          });
          return;
        case "SwapRouteSwapImpactTooHigh":
          methods.setError("toQuantity", {
            message: t("priceImpactTooHigh"),
            type: "warning"
          });
          return;
        case "SwapRouteUnsupportedChainError":
          methods.setError("root.swapError", {
            message: t("unsupportedChain"),
            type: "critical"
          });
          return;
        case "SwapRouteInvalidAddressError":
          methods.setError("recipient", {
            message: t("invalidAddress"),
            type: "critical"
          });
          return;
        case "SwapRouteUnsupportedCurrencyError":
          methods.setError("toCurrency", {
            message: t("tokenNotSupported"),
            type: "critical"
          });
          return;
        case "SwapRouteTemporarilyRestrictedError":
          methods.setError("root.swapError", {
            message: t("routeTemporarilyUnavailable"),
            type: "warning"
          });
          return;
        default:
          methods.setError("root.swapError", {
            message: t("unknownError"),
            type: "critical"
          });
          return;
      }
    }
  }, [data?.tokenSwap, methods, t, transactionMode, fromQuantity, toQuantity]);
  const isLoadingQuote = fetching || transactionMode === "sell" && Boolean(debouncedFromQuantity) && debouncedFromQuantity !== fromQuantity || transactionMode === "buy" && Boolean(debouncedToQuantity) && debouncedToQuantity !== toQuantity;

  // Define container for CurrencySelectSheet to render in
  const [container, setContainer] = useState(null);

  // Define onSubmit to set swap actions
  const [actions, setActions] = useState([]);
  const handleSubmit = useCallback(async () => {
    if (isLoadingQuote) {
      return;
    }
    setActions(data?.tokenSwap.swapActions.actions ?? []);
    if (data?.tokenSwap.swapActions.errors && data.tokenSwap.swapActions.errors.length > 0) {
      methods.setError("root.swapError", {
        message: t("unknownError"),
        type: "critical"
      });
    }
  }, [isLoadingQuote, data?.tokenSwap.swapActions.errors, data?.tokenSwap.swapActions.actions, methods, t]);
  const isSmartContractWallet = useIsSmartContractWallet();
  const [smartContractWalletWarningModalOpen, setSmartContractWalletWarningModalOpen] = useState(false);
  const onSubmit = useCallback(async () => {
    const {
      fromCurrency: fromCurrency_0,
      toCurrency: toCurrency_0
    } = methods.getValues();
    if (isSmartContractWallet && fromCurrency_0.chainIdentifier !== toCurrency_0?.chainIdentifier) {
      setSmartContractWalletWarningModalOpen(true);
    } else {
      return await handleSubmit();
    }
  }, [handleSubmit, isSmartContractWallet, methods]);

  // Reset calculated quantity when input changes and quote is loading
  /* useEffect(() => {
    if (isLoadingQuote) {
      if (transactionMode === "buy") {
        methods.setValue("fromQuantity", 0)
      } else {
        methods.setValue("toQuantity", 0)
      }
      setActions([])
    }
  }, [isLoadingQuote, methods, transactionMode]) */

  // sync auto slippage
  useEffect(() => {
    if (isAutoSlippageEnabled) {
      methods.setValue("maxSlippage", data?.tokenSwap?.quote?.slippageTolerance ?? getDefaultSlippage(chainArch), {
        shouldValidate: true
      });
    }
  }, [isAutoSlippageEnabled, data?.tokenSwap?.quote?.slippageTolerance, chainArch, methods]);
  const getIsQuoteValid = useCallback(swapQuote_0 => {
    const swapRoute_0 = swapQuote_0.swapRoutes[0];
    if (swapRoute_0.__typename !== "SwapRoute") {
      return false;
    }
    if (swapRoute_0.fromAsset.__typename !== "Currency" || swapRoute_0.toAsset.__typename !== "Currency") {
      return false;
    }

    // Reject quote if current form values are 0 or undefined (indicates recent mode change)
    // Only check the relevant quantity for the current mode
    if (transactionMode === "buy") {
      if (toQuantity === 0 || toQuantity === undefined) {
        return false;
      }
    } else if (fromQuantity === 0 || fromQuantity === undefined) {
      return false;
    }
    const currenciesMatch = areCurrenciesEqual(fromCurrency, readCurrencyIdentifierFragment(swapRoute_0.fromAsset)) && toCurrency && areCurrenciesEqual(toCurrency, readCurrencyIdentifierFragment(swapRoute_0.toAsset));
    if (transactionMode === "buy") {
      // Validate that we have a valid quote for the right currencies
      // Don't require exact quantity match due to debouncing race conditions
      return currenciesMatch && toQuantity !== undefined && toQuantity > 0 && swapRoute_0.toQuantity && swapRoute_0.toQuantity !== "0";
    }
    return currenciesMatch && debouncedFromQuantity && fromQuantity !== undefined && fromQuantity > 0 && bn(swapRoute_0.fromQuantity, swapRoute_0.fromAsset.decimals).isEqualTo(debouncedFromQuantity);
  }, [debouncedFromQuantity, debouncedToQuantity, fromCurrency, toCurrency, fromQuantity, toQuantity, transactionMode]);
  useEffect(() => {
    if (!(isLoadingQuote || error)) {
      if (data?.tokenSwap && getIsQuoteValid(data.tokenSwap.quote)) {
        setSwapQuote(data.tokenSwap.quote);
      } else {
        setSwapQuote(undefined);
      }
    } else if (error) {
      setSwapQuote(undefined);
      methods.setError("toQuantity", {
        message: "Unknown error: Failed to find route"
      });
    } else {
      setSwapQuote(undefined);
    }
  }, [data?.tokenSwap, error, getIsQuoteValid, isLoadingQuote, methods]);
  const swapRoute_1 = swapQuote?.swapRoutes[0];
  const swapProvider = swapRoute_1?.__typename === "SwapRoute" ? swapRoute_1.swapProvider : undefined;
  const [receipt, waitForCompletion] = useSwapReceipt({
    fromCurrency,
    toCurrency,
    fromQuantity,
    toQuantity,
    swapProvider: swapProvider ?? undefined
  });
  const isVisible = useVisibilityChange();

  // Poll for quote updates
  useEffect(() => {
    if (data && !isLoadingQuote && actions.length === 0 && methods.formState.isValid && Object.keys(methods.formState.errors).length === 0 && isVisible) {
      const interval = setInterval(() => {
        reexecuteQuery({
          requestPolicy: "network-only"
        });
      }, 10_000);
      return () => clearInterval(interval);
    }
  }, [data, reexecuteQuery, isLoadingQuote, actions, methods, isVisible]);
  const reset = useCallback(() => {
    // Clear form values
    methods.setValue("fromQuantity", 0);
    methods.setValue("toQuantity", 0);
    // Clear internal state
    setSwapQuote(undefined);
    setActions([]);
    setActionsStatus({
      isPending: false,
      isLoading: false,
      isDone: false
    });
  }, [methods]);
  const isCurrencySelectionDisabled = useMemo(() => {
    const hasFromRestrictions = fromCurrency?.restrictSwapsToNativeTokens || fromCurrency?.restrictSwapsToSameChain;
    const hasToRestrictions = toCurrency?.restrictSwapsToNativeTokens || toCurrency?.restrictSwapsToSameChain;
    return Boolean(isNFTStrategyCurrencySelectionDisabled && (hasFromRestrictions || hasToRestrictions));
  }, [fromCurrency, toCurrency, isNFTStrategyCurrencySelectionDisabled]);
  const toggleAutoSlippage = () => {
    setIsAutoSlippageEnabled(prev => !prev);
  };
  const [actionsStatus, setActionsStatus] = useState({
    isPending: false,
    isLoading: false,
    isDone: false
  });
  const contextValue = useMemo(() => ({
    isLoadingQuote,
    container,
    actions,
    quote: swapQuote,
    receipt,
    variant,
    isLoadingInitialToCurrency: isLoadingInitialToCurrency || isLoadingInitialToCurrencyData,
    isLoadingInitialFromCurrency: isLoadingInitialFromCurrency || isLoadingInitialFromCurrencyData,
    hasQuoteError: Boolean(data?.tokenSwap.quote.swapRoutes.some(route => route.__typename !== "SwapRoute")),
    isCurrencySelectionDisabled,
    isAutoSlippageEnabled,
    shouldShowConfirmationModal,
    actionsStatus,
    source,
    transactionMode,
    setTransactionMode,
    setActionsStatus,
    waitForCompletion,
    toggleAutoSlippage,
    setContainer,
    onSubmit,
    setShouldShowConfirmationModal,
    reset
  }), [isLoadingQuote, container, actions, swapQuote, receipt, variant, isLoadingInitialToCurrency, isLoadingInitialToCurrencyData, isLoadingInitialFromCurrency, isLoadingInitialFromCurrencyData, data?.tokenSwap.quote.swapRoutes, isAutoSlippageEnabled, isCurrencySelectionDisabled, shouldShowConfirmationModal, actionsStatus, source, transactionMode, setTransactionMode, setActionsStatus, toggleAutoSlippage, setShouldShowConfirmationModal, waitForCompletion, onSubmit, reset]);
  return <SwapContext.Provider value={contextValue}>
      <FormProvider {...methods}>{children}</FormProvider>
      <SmartContractWalletWarningModal onCancel={() => {
      setSmartContractWalletWarningModalOpen(false);
    }} onContinue={() => {
      setSmartContractWalletWarningModalOpen(false);
      handleSubmit();
    }} open={smartContractWalletWarningModalOpen} />
    </SwapContext.Provider>;
}
const useDefaultFormValues = t0 => {
  const $ = _c(15);
  const {
    isUsdInputMode,
    transactionMode
  } = t0;
  const address = useAddress();
  const {
    fromCurrency,
    toCurrency
  } = useDefaultSwapCurrencies();
  const chainArch = useChainArch();
  let t1;
  if ($[0] !== chainArch) {
    t1 = getDefaultSlippage(chainArch);
    $[0] = chainArch;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const defaultSlippage = t1;
  let t2;
  if ($[2] !== chainArch) {
    t2 = getMaxSlippageStorageKey(chainArch);
    $[2] = chainArch;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [storedMaxSlippage] = useLocalStorage(t2, defaultSlippage);
  const t3 = transactionMode === "sell" ? Number.NaN : 0;
  const t4 = transactionMode === "buy" ? Number.NaN : 0;
  let t5;
  if ($[4] !== address) {
    t5 = {
      identifier: address,
      resolvedAddress: address,
      isResolving: false,
      currentAddress: address
    };
    $[4] = address;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== fromCurrency || $[7] !== isUsdInputMode || $[8] !== storedMaxSlippage || $[9] !== t3 || $[10] !== t4 || $[11] !== t5 || $[12] !== toCurrency || $[13] !== transactionMode) {
    t6 = {
      fromCurrency,
      fromQuantity: t3,
      toCurrency,
      toQuantity: t4,
      maxSlippage: storedMaxSlippage,
      customDestinationWalletEnabled: false,
      isUsdInputMode,
      transactionMode,
      recipient: t5
    };
    $[6] = fromCurrency;
    $[7] = isUsdInputMode;
    $[8] = storedMaxSlippage;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = toCurrency;
    $[13] = transactionMode;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
const useDefaultSwapCurrencies = () => {
  const $ = _c(21);
  const {
    getNativeCurrency,
    getWrappedNativeCurrency
  } = useChains();
  const {
    getCurrency,
    currencies
  } = useCurrencies();
  const chainArch = useChainArch();
  const chainIdentifier = useChainIdentifier() ?? "ethereum";
  let t0;
  if ($[0] !== chainArch || $[1] !== chainIdentifier || $[2] !== currencies || $[3] !== getCurrency || $[4] !== getNativeCurrency || $[5] !== getWrappedNativeCurrency) {
    t0 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const nativeChainCurrency = getNativeCurrency(chainIdentifier);
      const wrappedNativeChainCurrency = getWrappedNativeCurrency(chainIdentifier);
      let t1;
      if ($[7] !== chainIdentifier || $[8] !== currencies) {
        t1 = currencies.find(currency => currency.chainIdentifier === chainIdentifier) ?? currencies[0];
        $[7] = chainIdentifier;
        $[8] = currencies;
        $[9] = t1;
      } else {
        t1 = $[9];
      }
      const fallbackCurrency = t1;
      switch (chainArch) {
        case "SVM":
          {
            let t2;
            if ($[10] !== fallbackCurrency || $[11] !== getCurrency) {
              t2 = getCurrency({
                contractAddress: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
                chainIdentifier: "solana"
              }) ?? fallbackCurrency;
              $[10] = fallbackCurrency;
              $[11] = getCurrency;
              $[12] = t2;
            } else {
              t2 = $[12];
            }
            const t3 = nativeChainCurrency ? getCurrency(nativeChainCurrency) : undefined;
            let t4;
            if ($[13] !== t2 || $[14] !== t3) {
              t4 = {
                fromCurrency: t2,
                toCurrency: t3
              };
              $[13] = t2;
              $[14] = t3;
              $[15] = t4;
            } else {
              t4 = $[15];
            }
            t0 = t4;
            break bb0;
          }
        default:
          {
            let t2;
            if ($[16] !== chainIdentifier) {
              t2 = currency_0 => currency_0.symbol === "USDC" && currency_0.chainIdentifier === chainIdentifier;
              $[16] = chainIdentifier;
              $[17] = t2;
            } else {
              t2 = $[17];
            }
            const usdcCurrency = currencies.find(t2);
            const t3 = (nativeChainCurrency ? getCurrency(nativeChainCurrency) : undefined) ?? fallbackCurrency;
            const t4 = usdcCurrency ? getCurrency({
              contractAddress: usdcCurrency.contractAddress,
              chainIdentifier
            }) ?? (wrappedNativeChainCurrency ? getCurrency(wrappedNativeChainCurrency) : undefined) : wrappedNativeChainCurrency ? getCurrency(wrappedNativeChainCurrency) : undefined;
            let t5;
            if ($[18] !== t3 || $[19] !== t4) {
              t5 = {
                fromCurrency: t3,
                toCurrency: t4
              };
              $[18] = t3;
              $[19] = t4;
              $[20] = t5;
            } else {
              t5 = $[20];
            }
            t0 = t5;
            break bb0;
          }
      }
    }
    $[0] = chainArch;
    $[1] = chainIdentifier;
    $[2] = currencies;
    $[3] = getCurrency;
    $[4] = getNativeCurrency;
    $[5] = getWrappedNativeCurrency;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  if (t0 !== Symbol.for("react.early_return_sentinel")) {
    return t0;
  }
};